/**
 * Asset Management Dashboard
 * Central hub for viewing, filtering, and managing marketing assets
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Filter, Download, Edit2, Trash2, Copy, Eye,
  Mail, Image, Video, Globe, HelpCircle, Layout, Megaphone, Mic,
  ChevronRight, Grid, List, Calendar, Tag, User, Folder
} from 'lucide-react';
import { assetService } from '../services/assetService';
import {
  MarketingAsset,
  AssetType,
  AssetStatus,
  getAssetTypeIcon,
  getAssetTypeLabel
} from '../types/asset-metadata';

export default function AssetManagement() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState<MarketingAsset[]>([]);
  const [filteredAssets, setFilteredAssets] = useState<MarketingAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedType, setSelectedType] = useState<AssetType | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<AssetStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());

  // Load assets on mount
  useEffect(() => {
    loadAssets();
  }, []);

  // Filter assets when criteria change
  useEffect(() => {
    filterAssets();
  }, [assets, selectedType, selectedStatus, searchQuery]);

  const loadAssets = async () => {
    try {
      setLoading(true);
      const result = await assetService.listAssets({ limit: 100 });
      setAssets(result.assets);
    } catch (error) {
      console.error('Failed to load assets:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAssets = () => {
    let filtered = [...assets];

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(a => a.type === selectedType);
    }

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(a => a.status === selectedStatus);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a =>
        a.title.toLowerCase().includes(query) ||
        a.description?.toLowerCase().includes(query) ||
        a.metadata.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    setFilteredAssets(filtered);
  };

  const handleDeleteAsset = async (assetId: string) => {
    if (confirm('Are you sure you want to archive this asset?')) {
      try {
        await assetService.deleteAsset(assetId);
        await loadAssets();
      } catch (error) {
        console.error('Failed to delete asset:', error);
        alert('Failed to delete asset');
      }
    }
  };

  const handleDuplicateAsset = async (assetId: string) => {
    try {
      await assetService.duplicateAsset(assetId);
      await loadAssets();
    } catch (error) {
      console.error('Failed to duplicate asset:', error);
      alert('Failed to duplicate asset');
    }
  };

  const getStatusColor = (status: AssetStatus) => {
    const colors: Record<AssetStatus, string> = {
      draft: 'bg-gray-100 text-gray-700',
      in_review: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      published: 'bg-blue-100 text-blue-700',
      archived: 'bg-red-100 text-red-700',
    };
    return colors[status];
  };

  const getTypeIcon = (type: AssetType) => {
    const icons: Record<AssetType, React.ReactNode> = {
      email: <Mail className="w-5 h-5" />,
      image: <Image className="w-5 h-5" />,
      video: <Video className="w-5 h-5" />,
      seo_content: <Globe className="w-5 h-5" />,
      faq: <HelpCircle className="w-5 h-5" />,
      site_content: <Layout className="w-5 h-5" />,
      ad: <Megaphone className="w-5 h-5" />,
      podcast: <Mic className="w-5 h-5" />,
    };
    return icons[type];
  };

  // Asset Grid Card
  const AssetGridCard = ({ asset }: { asset: MarketingAsset }) => (
    <div
      className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => navigate(`/assets/${asset.id}/edit`)}
    >
      {/* Asset Preview */}
      <div className="h-48 bg-gray-100 rounded-t-lg flex items-center justify-center relative">
        <div className="text-gray-400">
          {getTypeIcon(asset.type)}
        </div>
        <span className={`absolute top-2 right-2 px-2 py-1 text-xs rounded ${getStatusColor(asset.status)}`}>
          {asset.status}
        </span>
      </div>

      {/* Asset Info */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 truncate">{asset.title}</h3>
        <p className="text-sm text-gray-500 mt-1 truncate">{asset.description}</p>

        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            {getTypeIcon(asset.type)}
            <span>{getAssetTypeLabel(asset.type)}</span>
          </div>
          <div className="flex items-center space-x-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDuplicateAsset(asset.id);
              }}
              className="p-1 hover:bg-gray-100 rounded"
              title="Duplicate"
            >
              <Copy className="w-4 h-4 text-gray-500" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteAsset(asset.id);
              }}
              className="p-1 hover:bg-gray-100 rounded"
              title="Archive"
            >
              <Trash2 className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Tags */}
        {asset.metadata.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {asset.metadata.tags.slice(0, 3).map((tag, i) => (
              <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Asset List Row
  const AssetListRow = ({ asset }: { asset: MarketingAsset }) => (
    <tr
      className="hover:bg-gray-50 cursor-pointer"
      onClick={() => navigate(`/assets/${asset.id}/edit`)}
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <input
          type="checkbox"
          checked={selectedAssets.has(asset.id)}
          onChange={(e) => {
            e.stopPropagation();
            const newSelected = new Set(selectedAssets);
            if (e.target.checked) {
              newSelected.add(asset.id);
            } else {
              newSelected.delete(asset.id);
            }
            setSelectedAssets(newSelected);
          }}
          className="rounded"
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            {getTypeIcon(asset.type)}
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-gray-900">{asset.title}</p>
            <p className="text-sm text-gray-500">{asset.description}</p>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-gray-900">{getAssetTypeLabel(asset.type)}</span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(asset.status)}`}>
          {asset.status}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {asset.metadata.campaign || '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {new Date(asset.updated_at).toLocaleDateString()}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/assets/${asset.id}/edit`);
            }}
            className="text-indigo-600 hover:text-indigo-900"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDuplicateAsset(asset.id);
            }}
            className="text-gray-600 hover:text-gray-900"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteAsset(asset.id);
            }}
            className="text-red-600 hover:text-red-900"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Marketing Assets</h1>
              <p className="text-sm text-gray-500 mt-1">
                Manage your marketing content and creative assets
              </p>
            </div>
            <button
              onClick={() => navigate('/assets/create')}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5" />
              <span>Create Asset</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search assets..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center space-x-2 ml-4">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Filter Buttons */}
          <div className="flex items-center space-x-4">
            {/* Type Filter */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Type:</span>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as AssetType | 'all')}
                className="border rounded px-3 py-1 text-sm"
              >
                <option value="all">All Types</option>
                <option value="email">Email</option>
                <option value="image">Image</option>
                <option value="video">Video</option>
                <option value="seo_content">SEO Content</option>
                <option value="faq">FAQ</option>
                <option value="site_content">Site Content</option>
                <option value="ad">Advertisement</option>
                <option value="podcast">Podcast</option>
              </select>
            </div>

            {/* Status Filter */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Status:</span>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value as AssetStatus | 'all')}
                className="border rounded px-3 py-1 text-sm"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="in_review">In Review</option>
                <option value="approved">Approved</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Results Count */}
            <div className="flex-1 text-right">
              <span className="text-sm text-gray-500">
                {filteredAssets.length} {filteredAssets.length === 1 ? 'asset' : 'assets'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Assets Display */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 mb-4">No assets found</p>
            <button
              onClick={() => navigate('/assets/create')}
              className="text-blue-600 hover:text-blue-700 underline"
            >
              Create your first asset
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredAssets.map((asset) => (
              <AssetGridCard key={asset.id} asset={asset} />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedAssets(new Set(filteredAssets.map(a => a.id)));
                        } else {
                          setSelectedAssets(new Set());
                        }
                      }}
                      className="rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Asset
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Updated
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAssets.map((asset) => (
                  <AssetListRow key={asset.id} asset={asset} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}